import SvgIcon from '@home/components/SvgIcon';
import CardCommon from '@op/components/CardCommon';
import { sentimentOptions } from '@op/components/FilterCommon/constants/sentiment';
import GPT from '@op/components/GPT';
import Sentiment from '@op/components/Snippets/Sentiment';
import TableCommonHook from '@op/components/TableCommonHook';
import TableMultipleSelectFilter from '@op/components/TableMultipleSelectFilter';

import { DefaultImg } from '@op/constants/defaultImg';
import useLanguage from '@op/hooks/useLanguage';
import KeywordCard from '@op/pages/Performance/components/TopicKeyword/KeywordCard';
import TopicCard from '@op/pages/Performance/components/TopicKeyword/TopicCard';
import {
  useRequestComments,
  useRequestStats,
} from '@op/pages/RealtimeStreaming/hooks/useFeedsData';
import { IRealtimeStreamingDetail, LiveTimeType } from '@op/pages/RealtimeStreaming/type';
import { getLiveTimeTitle } from '@op/pages/RealtimeStreaming/utils';
import { DBO_FEEDS, DBO_V } from '@op/utils/format';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Segment, TabPanel, Tabs, Tag } from 'tea-component';
import './index.scss';
import TopicBox from '@op/components/TopicBox';
import { DBO_POINT } from '@op/operationLog/buriedPoint';
import { downloadAction } from '@op/pages/download/utils';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import { getFeedsContentText } from '@op/utils/languageValue';
import { LanguageType } from '@op/types/common';
import { useDispatch } from 'umi';
import { SAVE_PAGE_NAME } from '@op/components/GPT/constants';
import { SortBy } from 'tea-component/lib/table/addons';
import { ValidationHeaderNew } from '@op/components/TableCommonHook/columns/components/ValidationHeader';

export default function LiveChartsAnalysis({
  filter: params,
  liveTime,
  liveName,
  data,
  isGST
}: {
  filter: any;
  liveTime?: LiveTimeType;
  liveName: string;
  data: IRealtimeStreamingDetail;
  isGST: boolean;
}) {
  const { formatMessage } = useLanguage();
  const [value, setValue] = useState('live');
  const [selectTopic, setSelectTopic] = useState<string>('');
  const [selectKeyword, setSelectKeyword] = useState<{
    title: string;
    params: any[];
  }>({ title: '', params: [] });

  const formatData = (value: number | undefined) => {
    if (!value) {
      return '-';
    }
    return `${DBO_V.reviews(value, 0)}%`;
  };
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'content_length_no_space',
    order: 'desc',
  });

  const filter = useMemo(() => {
    const { live_id, ...otherInfo } = params;
    let filterInfo = {
      ...otherInfo,
      social_media_by_account: ['-99999'],
      social_media_by_keyword: ['-99999'],
      external_data_sources: ['-99999'],
    };

    if (isGST) {
      filterInfo = {
        ...filterInfo,
        content_urls: [data.live_url],
        social_media_by_account: [],
        social_media_by_keyword: [],
        external_data_sources: [],
      };
    } else {
      filterInfo = {
        ...filterInfo,
        live_ids: [live_id],
        feed_source: 'real_time_streaming',
      };
    }

    return filterInfo;
  }, [params, data]);

  const feedsFilter = useMemo(() => {
    return {
      ...filter,
      keyword: selectKeyword.params,
      topic: selectTopic ? [selectTopic] : [],
    };
  }, [filter, selectKeyword, selectTopic]);

  const { feedsStats } = useRequestStats({ filter: feedsFilter });

  const tabList = useMemo(() => {
    return [
      { text: formatMessage('Live Chats'), value: 'live' },
      {
        text: (
          <div className="dbo-inlineflex">
            <img src={DefaultImg.AI}></img>
            {formatMessage('AI Summary')}
          </div>
        ),
        value: 'ai',
      },
    ];
  }, [formatMessage]);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({
      type: 'opinionAIShortIdFilter/save',
      payload: { ...feedsFilter, [SAVE_PAGE_NAME]: 'Common_Page' },
    });
  }, []);

  return (
    <CardCommon
      className="dbo-live-streaming-content-analysis-wrapper"
      title={
        <div className="dbo-bubble-common-title">
          {formatMessage('Live Chats Analysis')}
          <span className="dbo-common-subtitle">
            ({formatMessage('Analysis Time Range')}:{getLiveTimeTitle(liveTime)}
            )
          </span>
        </div>
      }
    >
      <Row>
        <Col span={12}>
          <KeywordCard
            filter={filter}
            curFilter={'total'}
            hasKeywordDownPage={true}
            hasSubTitle={false}
            wordCloudChartHeight={278}
            lineHeight={306}
            tableHeight={256.5}
            statsDetailType={'Keyword'}
            hasTitleArrow={false}
            noTimeline
            openUserCache
            dealwithTimeLineParams={(params: any) => ({
              ...params,
              statistics_field: 'overview',
            })}
            title={formatMessage({ id: 'Keyword' })}
            className={'steam-feeds-reviews-detailed-keyword-card'}
            keywordClick={(data: any) => {
              setSelectKeyword(data);
              DBO_POINT.btn('ClickKeyword', value[0]);
            }}
          ></KeywordCard>
        </Col>
        <Col span={12}>
          <TopicCard
            filter={filter}
            hasTitleArrow={false}
            needToFeeds={false}
            curFilter={'total'}
            position={'right'}
            noTimeline
            openUserCache
            translateTopic={(text: string) => text}
            handleClickInfo={(data: any) => {
              setSelectTopic(data.seriesName);
              DBO_POINT.btn('ClickTopic', data.seriesName);
            }}
          // downloadFileName={'Realtime Streaming Details'}
          ></TopicCard>
        </Col>
      </Row>

      <CardCommon
        className="dbo-live-charts-info-cardCommon"
        title={
          <div className="dbo-live-charts-info-cardCommon-header">
            <Segment
              value={value}
              onChange={(value) => setValue(value)}
              options={tabList}
            />
            {selectKeyword.title && (
              <Tag onClose={() => setSelectKeyword({ title: '', params: [] })}>
                {formatMessage('Keyword')}: {selectKeyword.title}
              </Tag>
            )}
            {selectTopic && (
              <Tag onClose={() => setSelectTopic('')}>
                {formatMessage('Topic')}: {selectTopic}
              </Tag>
            )}
          </div>
        }
        contentCard
        hasDownload
        handleDownloadOnClick={() => {
          downloadAction({
            key: 'RealtimeStreamingDetailTable',
            filter: {
              ...feedsFilter,
              sort_item: sortBy.by,
              sort_model: sortBy.order,
            },
            fileName: `Live Chats Analysis`,
            needFilterTime: false,
          });
        }}
      >
        <div className={`dbo-sentiment-list`}>
          {[
            { key: `good_review`, sentiment: 5 },
            { key: `bad_review`, sentiment: 1 },
            { key: `neutral_review`, sentiment: 3 },
          ].map(({ key, sentiment }) => (
            <span className="dbo-sentiment-item" key={key}>
              <Sentiment
                iconClassName="dbo-sentiment-icon"
                showValue={false}
                sentiment={sentiment}
              />
              <span>{formatData(feedsStats?.[key] || 0)}</span>
            </span>
          ))}
        </div>

        <Tabs
          activeId={value}
          className="dbo-hide-tabs"
          destroyInactiveTabPanel={false}
          tabs={tabList.map(({ text, value }) => ({ label: text, id: value }))}
        >
          <TabPanel id={'live'}>
            <LiveStreamingTable
              sortBy={sortBy}
              setSortBy={setSortBy}
              filter={feedsFilter}
            ></LiveStreamingTable>
          </TabPanel>
          <TabPanel id={'ai'}>
            <GPT.PageCard
              className={'gpt-summary gpt-summary--realtime-streaming'}
              hoverNoShadow
              filter={feedsFilter}
              oriFeedsState={feedsStats}
              scenario="real_time_stream"
              hasStopBtn={true}
              autoRequest={false}
              saveName={`LiveChatsAnalysis-${liveName}`}
            >
              {(toggleVisible: any, { loading, total }: any) => {
                return (
                  <div className="gpt-summary-button">
                    <GPT.Button
                      disabled={total <= 0}
                      loading={false}
                      onClick={() => {
                        toggleVisible();
                        DBO_POINT.btn('CreateFiltersSummary');
                      }}
                    ></GPT.Button>
                  </div>
                );
              }}
            </GPT.PageCard>
          </TabPanel>
        </Tabs>
      </CardCommon>
    </CardCommon>
  );
}

const textMap: { [key in LanguageType]: string } = {
  en: 'English',
  zh: 'Chinese',
  origin: 'Original',
};
function LiveStreamingTable({
  filter,
  sortBy,
  setSortBy,
}: {
  filter: any;
  sortBy: any;
  setSortBy: any;
}) {
  const { formatMessage, isEn } = useLanguage();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const {
    feedsComments,
    loading,
    sentimentValue,
    setSelectSentiment,
    pageInfo,
    changePageInfo,
    changeSort,
    changeInvalid,
    invalid,
  } = useRequestComments({ filter, setSortBy, sortBy });

  const options = useMemo(() => {
    return sentimentOptions(formatMessage);
  }, [formatMessage]);

  const columns = useMemo(() => {
    return [
      {
        header: (
          <div className="dbo-tablecommon-header-title-flex">
            {formatMessage('Live Chats in {language} Language', {
              language: formatMessage(textMap[language || 'origin']),
            })}
            <SingleTranslateSelect
              defaultValue={language}
              onChange={(_key: string, value: LanguageType) =>
                setLanguage(value)
              }
            ></SingleTranslateSelect>
          </div>
        ),
        width: 600,
        key: 'title',
        render: (cvm: any) => {
          return <div>{getFeedsContentText(cvm, language)}</div>;
        },
      },
      {
        header: (
          <TableMultipleSelectFilter
            options={options.map((item) => ({
              ...item,
              value: String(item.value),
            }))}
            value={sentimentValue.map(String)}
            onChange={(value) => setSelectSentiment(value.map(Number))}
            listWidth={240}
            listHeight={185}
            needFormatMessage={false}
            button={(icon) => (
              <div
                className={`tag-filter-box ${sentimentValue?.length !== options?.length && 'active'
                  }`}
              >
                {formatMessage({ id: 'Sentiment' })}
                {icon}
              </div>
            )}
          />
        ),
        key: 'sentiment',
        width: 150,
        render: (cvm: any) => {
          return (
            <Sentiment
              showValue={false}
              sentiment={cvm.sentiment_rating}
            ></Sentiment>
          );
        },
      },
      {
        header: formatMessage('Topic'),
        key: 'topic',
        width: 250,
        render: (cvm: any) => {
          return (
            <TopicBox
              topicList={cvm.topic.split('|')}
              data={cvm}
              noEdit
              hasSentiment={false}
            ></TopicBox>
          );
        },
      },
      {
        key: 'sort_isvalid',
        width: 150,
        header: (
          <ValidationHeaderNew
            sort={sortBy}
            setSort={changeSort}
            options={[
              { text: 'Only High Validation', value: '2' },
              { text: 'High & Middle Validation', value: '1,2' },
              { text: 'Exclude Invalid', value: '1,2,3' },
            ]}
            selectValue={invalid}
            onChange={changeInvalid}
          ></ValidationHeaderNew>
        ),
        align: 'left',
        render: (cvm: any) => {
          return formatMessage(DBO_FEEDS.valid(cvm.isvalid));
        },
      },
      {
        key: 'content_length_no_space',
        width: isEn ? 200 : 130,
        header: formatMessage('Character Length'),
        align: 'right',
        render: (cvm: any) => {
          return cvm.content_length_no_space || 0;
        },
      },
      {
        header: formatMessage('Time'),
        key: 'comment_time',
        width: 250,
        render: (cvm: any) => {
          return <div>{cvm.comment_time}</div>;
        },
      },
    ];
  }, [sentimentValue, formatMessage, language, invalid, sortBy]);

  return (
    <TableCommonHook
      key={'conmut_uni'}
      records={feedsComments?.list || []}
      loading={loading}
      columns={columns}
      bordered
      sortInfo={[sortBy, changeSort]}
      total={feedsComments?.total || 0}
      pageInfo={pageInfo}
      setPageInfo={changePageInfo}
      sortColumns={['content_length_no_space', 'comment_time']}
    ></TableCommonHook>
  );
}
